import { User } from '@ravago/shared/authentication';
import { getLogger } from '@ravago/shared/logging/client';
import RevalidationTag from '@ravago/shared/page-data/enums/revalidation-tags/tags';
import { ContentfulCollectionName } from '@ravago/shared/page-data/models/contentful';
import { TypeProduct, TypeProductFields } from '@ravago/shared/page-data/models/contentful-generated';
import { NormalizedCollection, NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { BaseQuery } from '@ravago/shared/page-data/models/utils';
import { ContentfulUtils } from '@ravago/shared/page-data/utils/contentful';
import { DataMapperUtils } from '@ravago/shared/page-data/utils/data-mapper';
import { LogMessageUtils } from '@ravago/shared/page-data/utils/logging';
import { validateAndFilterProductEntries, validateProductEntry } from '@ravago/shared/page-data/validators';

import { ContentfulClientApi } from 'contentful';

import { cache } from '../cache/cache.utils';
import { createClient } from '../contentful/client';
import { MarketSegmentUtils } from '../market-segment/market-segment.utils';
import { QueryBuilder } from '../query-builder/query-builder';

/* c8 ignore start */
export class ContentfulProductUtils {
  static getProductCollection = cache(async (locale: string, query: BaseQuery, user?: User, overwriteClientApi?: ContentfulClientApi): Promise<NormalizedCollection<NormalizedProduct>> => {
    const contentfulClientApi = overwriteClientApi ?? createClient([RevalidationTag.products]);
    const contentType: ContentfulCollectionName = 'product';
    const { limit, page, search, orderBy, filterBy, productIds, producerIds, brandIds, processIds, industryIds, segmentIds } = query;

    const queryOptions = new QueryBuilder()
      .contentType(contentType)
      .limit(limit || 4)
      .skip((limit || 4) * (page && page > 0 ? page - 1 : 0))
      .search(search)
      .order(orderBy)
      .include(10)
      .locale(locale)
      .setArrayOptional('sys.id[in]', productIds)
      .setArrayOptional('fields.brand.sys.id[in]', brandIds)
      .setOptional('fields.processes.sys.id', processIds?.[0]);

    if (!user) {
      queryOptions.set('fields.isConflicting[ne]', 'true');
    }

    if (producerIds?.length) {
      const [producerId] = producerIds;

      queryOptions.set('fields.brand.sys.contentType.sys.id', 'brand');
      queryOptions.set('fields.brand.fields.producer.sys.id', producerId);
    }

    if (industryIds?.length) {
      const marketSegments = await MarketSegmentUtils.getMarketSegments(
        locale,
        {
          limit: 1000,
          filterBy,
          orderBy,
          industryIds
        },
        overwriteClientApi
      );

      const marketSegmentIds: Array<string> = marketSegments.items.map((marketSegment) => marketSegment.id) || [];

      queryOptions.set('fields.industrySegments.sys.id[in]', marketSegmentIds.join(','));
    }

    if (segmentIds?.length) {
      const [segmentId] = segmentIds;

      queryOptions.set('fields.industrySegments.sys.id', segmentId);
    }

    try {
      const entries = await contentfulClientApi.getEntries<TypeProductFields>(queryOptions.build());

      if (!entries.total) return ContentfulUtils.getDefaultCollection();

      const validEntries = validateAndFilterProductEntries(entries.items);

      if (!validEntries.length) return ContentfulUtils.getDefaultCollection();

      entries.items = validEntries;

      const response = DataMapperUtils.normalizeContentfulCollection<NormalizedCollection<NormalizedProduct>>(entries, DataMapperUtils.normalizeContentfulProduct);

      response.items = response.items.slice(0, limit || 4);

      return response;
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  });

  static getProductByEntryId = cache(async (locale: string, entryId?: string, overwriteClientApi?: ContentfulClientApi): Promise<NormalizedProduct | null> => {
    const contentfulClientApi = overwriteClientApi ?? createClient([RevalidationTag.products]);

    if (!entryId) return null;

    try {
      const entry = await contentfulClientApi.getEntry<TypeProductFields>(entryId, { locale, include: 10 });

      const validationResult = validateProductEntry(entry);

      if (!validationResult.success) {
        console.error(LogMessageUtils.validationFailed('PRODUCT'), validationResult.error.message);

        return null;
      }

      return DataMapperUtils.normalizeContentfulProduct(validationResult.data as unknown as TypeProduct);
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return null;
    }
  });
}

/* c8 ignore end */
